import { RevealGroup, RevealItem } from '@/components/Reveal';
import Typography from '@/components/ui/typography';
import { Link } from '@inertiajs/react';
import { MoveRight } from 'lucide-react';

export interface IndexEntry {
  title: string;
  summary: string;
  /** Route name. */
  link: string;
}

/*
 * The card grid both About hubs open with.
 *
 * About/Index and SpecialEvents/Index carried byte-identical markup for this —
 * and About/Index imported SpecialEvents/Index wholesale to render it twice on
 * one page, which meant an <h1> per card, eight of them, plus a second copy of
 * the Special Events heading.
 *
 * Numbered rather than illustrated: these entries have no images behind them,
 * and inventing some would be worse than owning the absence. The numerals also
 * carry the same counting device as the chapter spine the entries lead into, so
 * the hub and the pages beneath it read as one section.
 */
const SectionIndex = ({ entries }: { entries: IndexEntry[] }) => (
  <RevealGroup className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
    {entries.map((entry, index) => (
      <RevealItem key={entry.link} className="flex">
        <Link
          href={route(entry.link)}
          className="group relative flex w-full flex-col items-start gap-4 rounded-xl border bg-card p-6 shadow-card transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-pop md:p-7"
        >
          <span
            aria-hidden
            className="font-display text-4xl font-semibold tabular-nums text-thread transition-colors duration-300 group-hover:text-primary md:text-5xl"
          >
            {String(index + 1).padStart(2, '0')}
          </span>

          <div aria-hidden className="seam w-12" />

          <Typography.Display3 className="text-xl md:text-xl lg:text-2xl">
            {entry.title}
          </Typography.Display3>

          <Typography.Subtle className="line-clamp-4">
            {entry.summary}
          </Typography.Subtle>

          <span className="mt-auto inline-flex items-center gap-2 pt-4 text-sm font-semibold text-primary">
            Read more
            <MoveRight
              size={16}
              className="transition-transform duration-300 group-hover:translate-x-1"
            />
          </span>
        </Link>
      </RevealItem>
    ))}
  </RevealGroup>
);

export default SectionIndex;
